import { useCallback, useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import { connect, type ConnectedProps } from 'react-redux';
import {
  CharacterTextInput,
  type CharacterTextInputRef,
  IconValidationInvalid,
  InformationalScreen,
  LoadingModal,
  Modal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { type FormikHelpers, useFormik } from 'formik';
import { jwtDecode } from 'jwt-decode';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import {
  Button,
  FormContainer,
  MainContainer,
  TopHeaderBackArrow,
} from '~/components/utility';
import { ApiStatus } from '~/constants';
import { useRunOnceOnMountEffect } from '~/hooks';
import { type RootState } from '~/reducers';
import { requestPhoneCode, verifyPhoneCode } from '~/services/verify';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import { cleanPhoneNumber } from '~/utilities/cleanPhoneNumber';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';
import { persistAuthSession } from '~/utilities/session';

import phoneIcon from './phoneIcon.svg';

const mapStateToProps = ({
  env: { deviceId, osType, osVersion, appVersion },
  nav: { query },
}: RootState) => ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  query,
  phone: cleanPhoneNumber(query.phone),
  isReset: query.reset === 'true',
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const VERIFICATION_CODE_LENGTH = 6;

const testID = 'verify-phone';

const VerifyPhone = ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  query,
  phone,
  isReset,
}: Props) => {
  'use no memo'; // RC compiler disabled for this file due to useRunOnceOnMountEffect usage.

  const validationSchema = z.object({
    code: z
      .string({
        error: '',
      })
      .min(VERIFICATION_CODE_LENGTH, { error: '' })
      .regex(
        /^\d+$/,
        i18n.t(
          'VerifyPhone.content.verifyPhoneForm.formField.code.errors.codeValid',
        ),
      ),
  });

  const inputRef = useRef<CharacterTextInputRef>(null);

  const onSubmit = async ({
    code,
    phoneNumber,
    setFieldError,
  }: {
    phoneNumber: string;
    code: string;
    setFieldError: FormikHelpers<{ code: string }>['setFieldError'];
  }) => {
    try {
      const { status, data } = await verifyPhoneCode({
        otp: code,
        phoneNumber,
        osType,
        osVersion,
        appVersion,
        deviceId,
      });

      if (data?.status === 'active') {
        if (isReset) {
          mediator.publish('router:navigate', {
            url: '/password-setup',
            query: { ...query, phone: phoneNumber, reset: String(isReset) },
          });

          return;
        }

        Modal.show({
          dismissOnBackButtonPress: false,
          details: i18n.t(
            'SignIn.content.signInForm.modals.accountCreated.message',
          ),
          testID: buildTestId({
            testID,
            suffix: ['modal', 'account', 'created'],
          }),
          primaryAction: {
            label: i18n.t(
              'SignIn.content.signInForm.modals.accountCreated.okCap',
            ),
          },
        });

        mediator.publish('router:redirect', {
          url: '/login',
          query: { ...query, phone: phoneNumber },
        });

        return;
      }

      if (data?.status === 'archived') {
        setFieldError(
          'code',
          i18n.t(
            'VerifyPhone.content.verifyPhoneForm.formField.code.errors.invalid',
          ),
        );

        Modal.show({
          dismissOnBackButtonPress: false,
          details: 'TODO: handle "archived" accounts',
          testID: buildTestId({
            testID,
            suffix: ['modal', 'archived', 'accounts'],
          }),
          primaryAction: {
            label: i18n.t('VerifyPhone.content.modals.notFound.okCap'),
          },
        });

        notifyApp('adc-webview:verify-phone', { status, data });

        return;
      }

      if (data?.status !== 'inactive') {
        setFieldError(
          'code',
          i18n.t(
            'VerifyPhone.content.verifyPhoneForm.formField.code.errors.invalid',
          ),
        );

        notifyApp('adc-webview:verify-phone', { status, data });

        return;
      }

      notifyApp('adc-webview:verify-phone', { status, data });

      const decodedToken = jwtDecode<{
        accountID: string;
        app: string;
        country: string;
        deviceID: string;
        exp: number;
        iat: number;
        jti: string;
        phoneNumber: string;
      }>(data.accessToken);

      persistAuthSession({
        access_token: data.accessToken,
        account_id: decodedToken.accountID,
        expires: decodedToken.exp,
      });

      mediator.publish('router:navigate', {
        url: '/password-setup',
        query: { ...query, phone: phoneNumber, reset: String(isReset) },
      });
    } catch (error) {
      const err = AxiosError.from(error);

      if (err.status === ApiStatus.NOT_FOUND) {
        Modal.show({
          dismissOnBackButtonPress: false,
          header: i18n.t('VerifyPhone.content.modals.notFound.title'),
          details: i18n.t('VerifyPhone.content.modals.notFound.message'),
          testID: buildTestId({ testID, suffix: ['modal', 'not', 'found'] }),
          primaryAction: {
            label: i18n.t('VerifyPhone.content.modals.notFound.okCap'),
            onPress: () => {
              mediator.publish('router:back');
            },
          },
        });
      } else {
        setFieldError(
          'code',
          i18n.t(
            'VerifyPhone.content.verifyPhoneForm.formField.code.errors.invalid',
          ),
        );
      }
    }
  };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    errors,
    touched,
    values,
    isSubmitting,
  } = useFormik({
    initialValues: {
      code: '',
    },
    initialErrors: {
      code: '',
    },
    initialTouched: {
      code: false,
    },
    validationSchema: toFormikValidationSchema(validationSchema),
    validateOnBlur: true,
    onSubmit: async ({ code }, { setFieldError }) => {
      if (!phone) {
        return;
      }

      await onSubmit({ code, phoneNumber: phone, setFieldError });
    },
  });

  const handleRequestCode = useCallback(async () => {
    if (!phone) {
      return;
    }

    try {
      const { data, message } = await requestPhoneCode({
        phoneNumber: phone,
        appVersion,
        deviceId,
        osType,
        osVersion,
      });

      notifyApp('adc-webview:request-otp', { data, message });
    } catch (error) {
      const err = AxiosError.from(error);

      notifyApp('adc-webview:request-otp', {
        status: err.status,
        error: err.message,
      });
    }
  }, [phone, appVersion, deviceId, osType, osVersion]);

  const handleNavigateBack = () => {
    mediator.publish('router:back');
  };

  const codeHasError =
    typeof errors.code !== 'undefined' &&
    errors.code !== '' &&
    (touched.code ?? false);

  // If phone number is not provided, go back
  useEffect(() => {
    if (!phone) {
      mediator.publish('router:back');
    }
  }, [phone]);

  useRunOnceOnMountEffect(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    useCallback(() => {
      // Focus on mount
      inputRef.current?.focus();
      // Request OTP on mount
      void handleRequestCode();
    }, []),
  );

  // Submit on valid input
  useEffect(() => {
    if (isValid && !isSubmitting) {
      inputRef.current?.blur();
      handleSubmit();
    }
  }, [isValid]);

  return (
    <MainContainer testID={testID}>
      {isSubmitting && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}

      <InformationalScreen
        testID={buildTestId({ testID, suffix: 'screen' })}
        topNavigationProps={{
          title: i18n.t('VerifyPhone.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="nav-back" />,
            label: 'Go Back',
            onPress: handleNavigateBack,
          },
          testID: 'nav-back',
        }}
      >
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
        >
          <InformationalScreen.Content>
            <Stack
              width={94}
              height={94}
              marginTop={-16}
              alignSelf="center"
              backgroundColor="$neutral.noChangeWhite"
              borderRadius={94}
            >
              <img
                src={phoneIcon}
                alt="Phone with text message"
                width="100%"
                height="auto"
              />
            </Stack>

            <Text testID={buildTestId({ testID, suffix: ['phone', 'number'] })}>
              <Trans
                i18n={i18n}
                i18nKey="VerifyPhone.subtitle"
                values={{ phone }}
                components={{
                  Bold: <Text fontWeight="bold" />,
                }}
              />
            </Text>

            <FormContainer hasHiddenUserInput>
              <Stack alignSelf="center">
                <CharacterTextInput
                  ref={inputRef}
                  length={VERIFICATION_CODE_LENGTH}
                  testID={buildTestId({ testID, suffix: ['code', 'input'] })}
                  value={values.code}
                  onValueChange={handleChange('code')}
                  error={codeHasError}
                  supportingIconConfig={
                    codeHasError
                      ? { icon: <IconValidationInvalid /> }
                      : undefined
                  }
                  supportingText={codeHasError ? errors.code : undefined}
                  // @ts-expect-error Parallax doesn't type "onBlur" well, but accepts it
                  onBlur={handleBlur('code')}
                />
              </Stack>
            </FormContainer>

            <Button
              variant="tertiary"
              label={i18n.t(
                'VerifyPhone.content.verifyPhoneForm.links.resend.label',
              )}
              onPress={() => void handleRequestCode()}
              testID={buildTestId({ testID, suffix: ['resend', 'code'] })}
            />
          </InformationalScreen.Content>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(VerifyPhone);
